import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
  Alert,
  Snackbar,
  Stack,
  Chip
} from '@mui/material';
import {
  Stars as StarsIcon,
  CardGiftcard as GiftIcon,
  CheckCircleOutline as ActiveIcon,
  HighlightOff as InactiveIcon
} from '@mui/icons-material';
import api from '../api/api';

const PRIMARY_COLOR = '#4f46e5';

const PuntosCuentaModal = ({ open, onClose, clienteId, clienteNombre }) => {
  const [cuenta, setCuenta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Cargar/Inicializar la cuenta de puntos del cliente
  const fetchCuentaPuntos = useCallback(async () => {
    if (!clienteId || !open) return;
    setLoading(true);
    try {
      const response = await api.get(`/puntos-cuenta/cliente/${clienteId}`);
      setCuenta(response.data);
    } catch (err) {
      showSnackbar(err.response?.data?.detail || 'Error al obtener la cuenta de puntos', 'error');
    } finally {
      setLoading(false);
    }
  }, [clienteId, open]);

  useEffect(() => {
    if (open) {
      fetchCuentaPuntos();
    } else {
      setCuenta(null);
    }
  }, [open, fetchCuentaPuntos]);

  // Cambiar el estado activo/inactivo de la cuenta
  const handleToggleEstado = async () => {
    if (!cuenta) return;
    setUpdating(true);
    try {
      const response = await api.put(`/puntos-cuenta/${cuenta.id_cuenta_puntos}/estado`);
      setCuenta(response.data);
      showSnackbar(`Cuenta ${response.data.activo ? 'activada' : 'desactivada'} exitosamente`);
    } catch (err) {
      showSnackbar(err.response?.data?.detail || 'Error al actualizar el estado de la cuenta', 'error');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <StarsIcon sx={{ color: PRIMARY_COLOR }} />
          <Box flex={1}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              Programa de Puntos
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {clienteNombre || `Cliente ID: ${clienteId}`}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : cuenta ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Tarjeta Resumen de Puntos */}
              <Card
                variant="outlined"
                sx={{
                  bgcolor: cuenta.activo ? '#f8fafc' : '#f1f5f9',
                  borderColor: cuenta.activo ? '#cbd5e1' : '#e2e8f0',
                  opacity: cuenta.activo ? 1 : 0.65
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">
                      Estado de la Cuenta
                    </Typography>
                    <Chip
                      size="small"
                      icon={cuenta.activo ? <ActiveIcon fontSize="small" /> : <InactiveIcon fontSize="small" />}
                      label={cuenta.activo ? 'Activa' : 'Inactiva'}
                      color={cuenta.activo ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
                    {/* Puntos Acumulados */}
                    <Box textAlign="center" sx={{ p: 1, bgcolor: '#ffffff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Acumulados
                      </Typography>
                      <Typography variant="h5" fontWeight={800} color={PRIMARY_COLOR}>
                        {cuenta.puntos_acumulados ?? 0}
                      </Typography>
                    </Box>

                    {/* Puntos Canjeados */}
                    <Box textAlign="center" sx={{ p: 1, bgcolor: '#ffffff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Canjeados
                      </Typography>
                      <Typography variant="h5" fontWeight={800} color="text.secondary">
                        {cuenta.puntos_canjeados ?? 0}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Puntos Disponibles */}
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: PRIMARY_COLOR, borderRadius: 1.5, color: '#ffffff', textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ opacity: 0.9 }} display="block">
                      Saldo Disponible para Canje
                    </Typography>
                    <Typography variant="h4" fontWeight={800}>
                      {(cuenta.puntos_acumulados || 0) - (cuenta.puntos_canjeados || 0)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Control para Activar/Desactivar Cuenta */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={cuenta.activo}
                      onChange={handleToggleEstado}
                      disabled={updating}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={500}>
                      Permitir acumulación y canjes
                    </Typography>
                  }
                />
                {updating && <CircularProgress size={18} />}
              </Box>
            </Box>
          ) : (
            <Alert severity="warning">No se pudo cargar la información de la cuenta.</Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PuntosCuentaModal;